// WebRTC Configuration Utility
// This file provides centralized configuration for WebRTC settings

const resolveApiBase = (opts = {}) => {
  const { turnCredentialsUrl, appendApiPath } = opts;

  if (turnCredentialsUrl) {
    try {
      return new URL(turnCredentialsUrl, window.location.origin).toString().replace(/\/$/, '');
    } catch {
      return turnCredentialsUrl;
    }
  }

  const explicit = process.env.REACT_APP_API_URL;
  if (explicit) {
    try {
      const resolved = new URL(explicit, window.location.origin);
      return `${resolved.protocol}//${resolved.host}`;
    } catch {
      // fall through
    }
  }

  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return 'http://localhost:8000';
  }

  return appendApiPath ? `${window.location.origin}/api` : window.location.origin;
};

const fetchTurnCredentials = async () => {
  try {
    const turnUrl = process.env.REACT_APP_TURN_CREDENTIALS_URL;
    const base = resolveApiBase(turnUrl ? { turnCredentialsUrl: turnUrl } : {});
    const credsUrl =
      process.env.REACT_APP_TURN_CREDENTIALS_URL ||
      `${base}/turn-credentials`;

    const res = await fetch(credsUrl, { method: 'GET' });
    if (!res.ok) throw new Error(`TURN fetch failed: ${res.status}`);

    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (err) {
      throw new Error(`TURN fetch returned non-JSON payload: ${text.substring(0, 80)}…`);
    }

    if (data && Array.isArray(data.iceServers)) return data;
    return null;
  } catch (e) {
    console.warn('TURN credentials fetch failed:', e);
    return null;
  }
};

const getWebRTCConfig = () => {
  // Environment-aware configuration
  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // STUN/TURN server configuration
  let iceServers = [
    // Google STUN servers
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' }
  ];

  // Override with environment variable if provided
  const envStunTurnConfig = process.env.REACT_APP_STUN_TURN_CONFIG;
  if (envStunTurnConfig) {
    try {
      const config = JSON.parse(envStunTurnConfig);
      if (config.iceServers && Array.isArray(config.iceServers)) {
        iceServers = config.iceServers;
      }
    } catch (e) {
      console.warn('Failed to parse REACT_APP_STUN_TURN_CONFIG:', e);
    }
  }

  // Fetch dynamic TURN credentials (Metered) via backend; fallback to public TURN
  fetchTurnCredentials().then((turnData) => {
    if (turnData && Array.isArray(turnData.iceServers)) {
      const existing = new Set(iceServers.map(server => `${server.urls}|${server.username || ''}|${server.credential || ''}`));
      turnData.iceServers.forEach(server => {
        const key = `${server.urls}|${server.username || ''}|${server.credential || ''}`;
        if (!existing.has(key)) {
          iceServers.push(server);
          existing.add(key);
        }
      });
    } else {
      iceServers.push(
        { urls: 'turn:global.relay.metered.ca:80', username: 'openrelayproject', credential: 'openrelayproject' },
        { urls: 'turn:global.relay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
        { urls: 'turn:global.relay.metered.ca:3478?transport=udp', username: 'openrelayproject', credential: 'openrelayproject' }
      );
    }
  }).catch(() => {});

  // WebSocket signaling server URLs
  const getSignalingUrls = (path) => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const candidatesSet = new Set();

    const pushCandidate = (url) => {
      if (!url) return;
      try {
        const normalized = new URL(url, window.location.origin).toString();
        candidatesSet.add(normalized);
      } catch {
        // ignore malformed
      }
    };

    const apiBase = resolveApiBase({ appendApiPath: true });
    const apiWsBase = `${apiBase}/ws/${path}`;

    // Check for environment variable first
    const envSignalingServer = process.env.REACT_APP_SIGNALING_SERVER;
    if (envSignalingServer) {
      // Use environment variable if provided
      pushCandidate(`${envSignalingServer}/${path}`);
    }

    // API-relative WS path (covers nginx /api prefix setups)
    pushCandidate(apiWsBase);

    // Origin root (nginx proxy without /api prefix)
    pushCandidate(`${proto}://${window.location.host}/ws/${path}`);

    if (isDev) {
      // Development URLs
      pushCandidate(`${proto}://localhost:8000/ws/${path}`);
      pushCandidate(`${proto}://127.0.0.1:8000/ws/${path}`);
    }

    // Live deployment URLs - prioritize live domain
    pushCandidate(`wss://app.neurolonic.com/ws/${path}`);

    return Array.from(candidatesSet);
  };

  return {
    iceServers,
    getSignalingUrls,
    isDev,
    // Peer connection configuration
    peerConnectionConfig: {
      iceServers,
      iceCandidatePoolSize: 10
    }
  };
};

// Helper function to create a WebSocket connection with fallback
export const createWebSocketConnection = async (path, timeout = 4000) => {
  const config = getWebRTCConfig();
  const candidates = config.getSignalingUrls(path);

  for (const url of candidates) {
    let ws = null;
    try {
      ws = new WebSocket(url);
      await new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          reject(new Error(`WebSocket timeout for: ${url}`));
        }, timeout);

        ws.onopen = () => {
          clearTimeout(timer);
          resolve();
        };

        ws.onerror = (error) => {
          clearTimeout(timer);
          reject(new Error(`WebSocket error for: ${url}: ${error.message || error}`));
        };

        ws.onclose = () => {};
      });
      return ws;
    } catch (e) {
      try { ws && ws.close(); } catch {}
      continue;
    }
  }

  const allUrls = candidates.join(', ');
  throw new Error(`Unable to establish WebSocket connection to any of the following URLs: ${allUrls}`);
};

// Helper function to create RTCPeerConnection with proper configuration
export const createPeerConnection = () => {
  const config = getWebRTCConfig();
  return new RTCPeerConnection(config.peerConnectionConfig);
};

// Diagnostic utilities removed — connection failures surface naturally via WebSocket errors
